import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { Plus, Edit, Trash2, Loader2, BookOpen, Filter, X, Search, ChevronsUpDown, Check, User, GraduationCap, Mail, Hash, Building2, BookMarked, ChevronRight, ChevronLeft } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { cn } from '../ui/utils';
import { Faculty } from '../../types';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const ACADEMIC_RANKS = [
  'Instructor I', 'Instructor II', 'Instructor III',
  'Assistant Professor I', 'Assistant Professor II', 'Assistant Professor III', 'Assistant Professor IV',
  'Associate Professor I', 'Associate Professor II', 'Associate Professor III', 'Associate Professor IV', 'Associate Professor V',
  'Professor I', 'Professor II', 'Professor III', 'Professor IV', 'Professor V', 'Professor VI',
  'University Professor',
  'Visiting Professor',
];

export function FacultyManagement() {
  const { accessToken, getValidToken } = useAuth();
  const [faculty, setFaculty] = useState<Faculty[]>([]);
  const [subjects, setSubjects] = useState<any[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingFaculty, setEditingFaculty] = useState<Faculty | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [filterFacultyName, setFilterFacultyName] = useState<string>('');
  const [filterCourseCode, setFilterCourseCode] = useState<string>('');
  const [filterCourseName, setFilterCourseName] = useState<string>('');
  const [specSearch, setSpecSearch] = useState('');
  const [positionOpen, setPositionOpen] = useState(false);
  const [formStep, setFormStep] = useState(1);
  const TOTAL_STEPS = 3;
  const [formData, setFormData] = useState({
    idNumber: '',
    name: '',
    email: '',
    department: '',
    specialization: [] as string[],
    position: '',
    maxUnits: 12
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = await getValidToken().catch(() => accessToken || '');
      const [facultyData, subjectsData, coursesData] = await Promise.all([
        api.getFaculty(token),
        api.getSubjects(token),
        api.getCourses(token)
      ]);
      
      // Add backward compatibility - normalize faculty data client-side
      const normalizedFaculty = (facultyData.faculty || []).map((f: any) => ({
        ...f,
        specialization: Array.isArray(f.specialization) ? f.specialization : (f.specialization ? [f.specialization] : []),
        maxUnits: f.maxUnits || 12
      }));
      
      setFaculty(normalizedFaculty);
      setSubjects(subjectsData.subjects || []);
      setCourses(coursesData.courses || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error instanceof Error) {
        toast.error(`Failed to load data: ${error.message}`);
      } else {
        toast.error('Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const token = await getValidToken();
      
      if (editingFaculty) {
        await api.updateFaculty(editingFaculty.id, formData, token);
        toast.success('Faculty member updated successfully');
      } else {
        await api.createFaculty(formData, token);
        toast.success('Faculty member created successfully');
      }
      
      await fetchData();
      handleDialogClose();
    } catch (error) {
      console.error('Error saving faculty:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to save faculty member';
      
      // Show helpful message for duplicate email errors
      if (errorMessage.includes('already registered') || errorMessage.includes('already exists')) {
        toast.error('This email address is already in use. The system will attempt to clean up and retry. Please try again in a moment.');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (facultyMember: Faculty) => {
    setEditingFaculty(facultyMember);
    setFormData({
      idNumber: facultyMember.idNumber,
      name: facultyMember.name,
      email: facultyMember.email,
      department: facultyMember.department || '',
      specialization: Array.isArray(facultyMember.specialization) ? facultyMember.specialization : (facultyMember.specialization ? [facultyMember.specialization] : []),
      position: facultyMember.position,
      maxUnits: facultyMember.maxUnits || 12
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (facultyId: string) => {
    if (!confirm('Are you sure you want to delete this faculty member?')) {
      return;
    }
    
    try {
      const token = await getValidToken();
      await api.deleteFaculty(facultyId, token);
      toast.success('Faculty member deleted successfully');
      await fetchData();
    } catch (error) {
      console.error('Error deleting faculty:', error);
      toast.error('Failed to delete faculty member');
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingFaculty(null);
    setSpecSearch('');
    setFormStep(1);
    setFormData({
      idNumber: '',
      name: '',
      email: '',
      department: '',
      specialization: [],
      position: '',
      maxUnits: 12
    });
  };

  const canAdvanceStep = (step: number): boolean => {
    if (step === 1) {
      return formData.idNumber.trim() !== '' && formData.name.trim() !== '' && formData.email.trim() !== '';
    }
    if (step === 2) {
      return formData.position.trim() !== '';
    }
    return true;
  };

  const STEP_CONFIG = [
    { step: 1, label: 'Personal', icon: User },
    { step: 2, label: 'Academic', icon: GraduationCap },
    { step: 3, label: 'Subjects', icon: BookMarked },
  ] as const;

  const getSubjectProgramCode = (courseId?: string) => {
    if (!courseId) return 'No program';
    const course = courses.find((item) => item.id === courseId);
    return course?.code || 'No program';
  };

  const resolveSpecializationSubjects = (token: string) => {
    if (!token) return [] as any[];

    const byId = subjects.find((s) => s.id === token);
    if (byId) return [byId];

    const byCode = subjects.filter((s) => s.code === token);
    return byCode.length === 1 ? byCode : [];
  };

  const getSpecializationLabel = (token: string) => {
    const resolved = resolveSpecializationSubjects(token);
    if (resolved.length === 1) {
      const subject = resolved[0];
      return `${subject.code} (${getSubjectProgramCode(subject.courseId)})`;
    }
    return token;
  };

  const filteredFaculty = faculty.filter((f) => {
    if (filterFacultyName && !f.name.toLowerCase().includes(filterFacultyName.toLowerCase())) return false;
    if (filterCourseCode) {
      const specs = Array.isArray(f.specialization) ? f.specialization : (f.specialization ? [f.specialization] : []);
      const matchesCode = specs.some((token) => {
        const resolved = resolveSpecializationSubjects(token);
        if (resolved.length === 1) {
          return resolved[0].code.toLowerCase().includes(filterCourseCode.toLowerCase());
        }
        return token.toLowerCase().includes(filterCourseCode.toLowerCase());
      });
      if (!matchesCode) return false;
    }
    if (filterCourseName) {
      const specs = Array.isArray(f.specialization) ? f.specialization : (f.specialization ? [f.specialization] : []);
      const matchesCourseName = specs.some((token) =>
        resolveSpecializationSubjects(token).some((subject) =>
          subject.name.toLowerCase().includes(filterCourseName.toLowerCase())
        )
      );
      if (!matchesCourseName) return false;
    }
    return true;
  });

  const hasActiveFilters = filterFacultyName !== '' || filterCourseCode !== '' || filterCourseName !== '';

  const clearFilters = () => {
    setFilterFacultyName('');
    setFilterCourseCode('');
    setFilterCourseName('');
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Faculty Management</CardTitle>
            <CardDescription>Manage faculty members and their information</CardDescription>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) handleDialogClose(); }}>
            <DialogTrigger asChild>
              {/*
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Add Faculty
              </Button>
              */}
            </DialogTrigger>
            <DialogContent
              className="max-w-2xl max-h-[85vh] flex flex-col overflow-y-auto p-0"
              onPointerDownOutside={(e) => e.preventDefault()}
              onInteractOutside={(e) => e.preventDefault()}
              onFocusOutside={(e) => e.preventDefault()}
            >
              <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4 bg-muted/30 border-b space-y-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                    {editingFaculty ? <Edit className="h-5 w-5 text-primary" /> : <Plus className="h-5 w-5 text-primary" />}
                  </div>
                  <div>
                    <DialogTitle className="text-lg font-semibold">
                      {editingFaculty ? 'Edit Faculty Member' : 'Add New Faculty Member'}
                    </DialogTitle>
                    <DialogDescription className="text-sm mt-0.5">
                      Step {formStep} of {TOTAL_STEPS} — {STEP_CONFIG[formStep - 1].label} Information
                    </DialogDescription>
                  </div>
                </div>
                {/* Step Indicator */}
                <div className="flex items-center gap-2">
                  {STEP_CONFIG.map(({ step, label, icon: Icon }) => (
                    <div key={step} className="flex items-center gap-2 flex-1">
                      <button
                        type="button"
                        onClick={() => {
                          if (step < formStep || canAdvanceStep(formStep)) setFormStep(step);
                        }}
                        className={cn(
                          "flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-all w-full justify-center",
                          step === formStep
                            ? "bg-primary text-primary-foreground shadow-sm"
                            : step < formStep
                            ? "bg-primary/15 text-primary hover:bg-primary/25 cursor-pointer"
                            : "bg-muted text-muted-foreground"
                        )}
                      >
                        <Icon className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">{label}</span>
                        <span className="sm:hidden">{step}</span>
                      </button>
                      {step < TOTAL_STEPS && (
                        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" />
                      )}
                    </div>
                  ))}
                </div>
              </DialogHeader>

              <div className="flex-1 min-h-0 flex flex-col">
                <div className={cn("flex-1 px-6 py-5", formStep === 3 ? "overflow-hidden flex flex-col" : "overflow-y-auto")}>
                  {/* ── Step 1: Personal Information ── */}
                  {formStep === 1 && (
                    <div className="space-y-5 animate-in fade-in-0 slide-in-from-right-2 duration-200">
                      <div>
                        <h3 className="text-base font-semibold tracking-tight text-foreground flex items-center gap-2">
                          <User className="h-4.5 w-4.5 text-primary" />
                          Personal Information
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">Enter the faculty member's basic details</p>
                      </div>
                      <Separator />
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="idNumber" className="text-sm font-medium flex items-center gap-1.5">
                            <Hash className="h-3.5 w-3.5 text-muted-foreground" />
                            ID Number <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            id="idNumber"
                            value={formData.idNumber}
                            onChange={(e) => setFormData({...formData, idNumber: e.target.value})}
                            placeholder="e.g., FAC-001"
                            className="h-10"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="name" className="text-sm font-medium flex items-center gap-1.5">
                            <User className="h-3.5 w-3.5 text-muted-foreground" />
                            Full Name <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            id="name"
                            value={formData.name}
                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                            placeholder="e.g., Dr. Juan Dela Cruz"
                            className="h-10"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email" className="text-sm font-medium flex items-center gap-1.5">
                            <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                            Email Address <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            id="email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                            placeholder="e.g., juan.delacruz@nemsu.edu.ph"
                            className="h-10"
                          />
                          <p className="text-xs text-muted-foreground">Faculty will use this email address to login.</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ── Step 2: Academic Information ── */}
                  {formStep === 2 && (
                    <div className="space-y-5 animate-in fade-in-0 slide-in-from-right-2 duration-200">
                      <div>
                        <h3 className="text-base font-semibold tracking-tight text-foreground flex items-center gap-2">
                          <GraduationCap className="h-4.5 w-4.5 text-primary" />
                          Academic Information
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">Set the academic rank, program, and teaching load</p>
                      </div>
                      <Separator />
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium flex items-center gap-1.5">
                            <GraduationCap className="h-3.5 w-3.5 text-muted-foreground" />
                            Academic Rank <span className="text-destructive">*</span>
                          </Label>
                          <Popover open={positionOpen} onOpenChange={setPositionOpen}>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                role="combobox"
                                aria-expanded={positionOpen}
                                className="w-full justify-between font-normal h-10"
                                type="button"
                              >
                                {formData.position || 'Select academic rank...'}
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                              <Command>
                                <CommandInput placeholder="Search academic rank..." />
                                <CommandList>
                                  <CommandEmpty>No rank found.</CommandEmpty>
                                  <CommandGroup>
                                    {ACADEMIC_RANKS.map((rank) => (
                                      <CommandItem
                                        key={rank}
                                        value={rank}
                                        onSelect={(value) => {
                                          setFormData({ ...formData, position: value });
                                          setPositionOpen(false);
                                        }}
                                      >
                                        <Check className={cn('mr-2 h-4 w-4', formData.position === rank ? 'opacity-100' : 'opacity-0')} />
                                        {rank}
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="department" className="text-sm font-medium flex items-center gap-1.5">
                            <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                            Program
                          </Label>
                          <Select value={formData.department} onValueChange={(value) => setFormData({...formData, department: value})}>
                            <SelectTrigger className="h-10">
                              <SelectValue placeholder="Select program" />
                            </SelectTrigger>
                            <SelectContent>
                              {courses.map((course) => (
                                <SelectItem key={course.id} value={course.code + ' - ' + course.name}>
                                  {course.code} - {course.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="maxUnits" className="text-sm font-medium flex items-center gap-1.5">
                            <BookOpen className="h-3.5 w-3.5 text-muted-foreground" />
                            Maximum Teaching Units <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            id="maxUnits"
                            type="number"
                            min="3"
                            max="30"
                            value={formData.maxUnits}
                            onChange={(e) => setFormData({...formData, maxUnits: parseInt(e.target.value) || 12})}
                            placeholder="e.g., 12"
                            className="h-10"
                          />
                          <p className="text-xs text-muted-foreground">Total units this faculty can teach per semester</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ── Step 3: Subject Specializations ── */}
                  {formStep === 3 && (() => {
                    const selectedUnits = formData.specialization.reduce((sum, token) => {
                      const byId = subjects.find((sub) => sub.id === token);
                      if (byId) return sum + (byId.units || 0);

                      const byCode = subjects.filter((sub) => sub.code === token);
                      if (byCode.length === 1) return sum + (byCode[0].units || 0);

                      return sum;
                    }, 0);
                    const maxUnits = formData.maxUnits || 12;
                    const unitsRemaining = maxUnits - selectedUnits;

                    const handleToggleSubject = (subjectId: string, units: number) => {
                      const isSelected = formData.specialization.includes(subjectId);
                      if (isSelected) {
                        setFormData({
                          ...formData,
                          specialization: formData.specialization.filter(s => s !== subjectId)
                        });
                      } else {
                        if (selectedUnits + units > maxUnits) {
                          toast.error(`Cannot add this subject. Adding ${units} unit${units === 1 ? '' : 's'} would exceed the maximum of ${maxUnits} units (${selectedUnits}/${maxUnits} used).`);
                          return;
                        }
                        setFormData({
                          ...formData,
                          specialization: [...formData.specialization, subjectId]
                        });
                      }
                    };

                    return (
                    <div className="flex flex-col gap-4 animate-in fade-in-0 slide-in-from-right-2 duration-200 min-h-0 h-full">
                      {/* Header - fixed */}
                      <div className="flex items-center justify-between flex-shrink-0">
                        <div>
                          <h3 className="text-base font-semibold tracking-tight text-foreground flex items-center gap-2">
                            <BookMarked className="h-4.5 w-4.5 text-primary" />
                            Subject Specializations
                          </h3>
                          <p className="text-sm text-muted-foreground mt-1">Select all subjects this faculty is qualified to teach</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Badge variant={selectedUnits > maxUnits ? 'destructive' : 'secondary'} className="text-xs font-medium">
                            {selectedUnits}/{maxUnits} units
                          </Badge>
                          {formData.specialization.length > 0 && (
                            <Badge variant="secondary" className="text-xs font-medium">
                              {formData.specialization.length} selected
                            </Badge>
                          )}
                        </div>
                      </div>
                      <Separator className="flex-shrink-0" />

                      {/* Selected badges - fixed above the list */}
                      {formData.specialization.length > 0 && (
                        <div className="flex flex-wrap gap-2 flex-shrink-0 max-h-24 overflow-y-auto p-2 bg-muted/30 rounded-md border border-dashed">
                          {formData.specialization.map((token) => (
                            <Badge
                              key={token}
                              variant="secondary"
                              className="text-xs gap-1.5 pr-1.5 pl-2 py-1 cursor-pointer hover:bg-destructive/10 hover:text-destructive transition-colors font-medium"
                              onClick={() => setFormData({
                                ...formData,
                                specialization: formData.specialization.filter(s => s !== token)
                              })}
                            >
                              {getSpecializationLabel(token)}
                              <X className="h-3 w-3" />
                            </Badge>
                          ))}
                        </div>
                      )}

                      {/* Search - fixed */}
                      <div className="relative flex-shrink-0">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Filter by code or name..."
                          value={specSearch}
                          onChange={(e) => setSpecSearch(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.preventDefault();
                          }}
                          className="pl-8 h-10"
                        />
                      </div>

                      {/* Subject list - scrollable, takes remaining space */}
                      {(() => {
                        const filteredSubjects = subjects.filter((subject) => {
                          if (!specSearch) return true;
                          const term = specSearch.toLowerCase();
                          return subject.code.toLowerCase().includes(term) || subject.name.toLowerCase().includes(term);
                        });
                        return (
                          <div className="flex-1 min-h-0 max-h-[320px] overflow-y-auto border rounded-md pr-1 sm:max-h-[360px]">
                            <div className="p-2 space-y-0.5">
                              {subjects.length === 0 ? (
                                <p className="text-sm text-muted-foreground py-8 text-center">No subjects available. Create subjects first.</p>
                              ) : filteredSubjects.length === 0 ? (
                                <p className="text-sm text-muted-foreground py-8 text-center">No subjects match your filter.</p>
                              ) : (
                                filteredSubjects.map((subject) => (
                                  <div
                                    key={subject.id}
                                    className={cn(
                                      "flex items-start gap-3 rounded-lg px-3 py-3 transition-colors border border-transparent",
                                      formData.specialization.includes(subject.id)
                                        ? "bg-primary/5 border-primary/20 cursor-pointer hover:bg-muted/50"
                                        : subject.units > unitsRemaining
                                          ? "opacity-50 cursor-not-allowed"
                                          : "cursor-pointer hover:bg-muted/50"
                                    )}
                                    onClick={() => handleToggleSubject(subject.id, subject.units)}
                                  >
                                    <Checkbox
                                      id={`subject-${subject.id}`}
                                      checked={formData.specialization.includes(subject.id)}
                                      disabled={!formData.specialization.includes(subject.id) && subject.units > unitsRemaining}
                                      className="mt-0.5 shrink-0"
                                      onCheckedChange={() => handleToggleSubject(subject.id, subject.units)}
                                    />
                                    <Label
                                      htmlFor={`subject-${subject.id}`}
                                      className="cursor-pointer flex-1 min-w-0"
                                    >
                                      <span className="grid min-w-0 grid-cols-1 gap-y-2 sm:grid-cols-[minmax(96px,120px)_minmax(0,1fr)] sm:gap-x-4 sm:gap-y-1">
                                        <span className="text-sm font-semibold text-foreground break-words sm:pt-0.5">
                                          {subject.code}
                                        </span>
                                        <span className="text-sm leading-6 text-foreground/85 break-words sm:pt-0.5">
                                          {subject.name}
                                        </span>
                                        <span className="flex min-w-0 flex-wrap items-center gap-2 sm:col-start-2">
                                          <span className="inline-flex w-fit items-center rounded-full bg-muted px-2 py-1 text-xs font-medium text-muted-foreground">
                                            {getSubjectProgramCode(subject.courseId)}
                                          </span>
                                          <span className="inline-flex w-fit items-center rounded-full bg-muted px-2 py-1 text-xs font-medium text-muted-foreground">
                                            {subject.units} unit{subject.units === 1 ? '' : 's'}
                                          </span>
                                        </span>
                                        </span>
                                    </Label>
                                  </div>
                                ))
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                    );
                  })()}
                </div>
              </div>

              {/* Footer Navigation */}
              <div className="flex items-center justify-between px-6 py-4 border-t bg-muted/20 flex-shrink-0">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => formStep === 1 ? handleDialogClose() : setFormStep(formStep - 1)}
                  disabled={submitting}
                  className="gap-1.5"
                >
                  <ChevronLeft className="h-4 w-4" />
                  {formStep === 1 ? 'Cancel' : 'Back'}
                </Button>
                <div className="flex items-center gap-1.5">
                  {Array.from({ length: TOTAL_STEPS }, (_, i) => (
                    <div
                      key={i}
                      className={cn(
                        "h-1.5 rounded-full transition-all",
                        i + 1 === formStep ? "w-6 bg-primary" : i + 1 < formStep ? "w-1.5 bg-primary/50" : "w-1.5 bg-muted-foreground/25"
                      )}
                    />
                  ))}
                </div>
                {formStep < TOTAL_STEPS ? (
                  <Button
                    type="button"
                    onClick={() => {
                      if (canAdvanceStep(formStep)) {
                        setFormStep(formStep + 1);
                      } else {
                        toast.error('Please fill in all required fields before continuing.');
                      }
                    }}
                    disabled={submitting}
                    className="gap-1.5"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="gap-1.5"
                  >
                    {submitting && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                    {editingFaculty ? 'Update' : 'Add'} Faculty
                  </Button>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {!loading && faculty.length > 0 && (
          <div className="mb-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Filter className="h-4 w-4" />
              Filters
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="h-6 px-2 text-xs">
                  <X className="mr-1 h-3 w-3" />
                  Clear
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Faculty Name</Label>
                <Input
                  placeholder="Search by name..."
                  value={filterFacultyName}
                  onChange={(e) => setFilterFacultyName(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Course Code</Label>
                <Input
                  placeholder="Search by course code..."
                  value={filterCourseCode}
                  onChange={(e) => setFilterCourseCode(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Course Name</Label>
                <Input
                  placeholder="Search by course name..."
                  value={filterCourseName}
                  onChange={(e) => setFilterCourseName(e.target.value)}
                />
              </div>
            </div>
          </div>
        )}
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : faculty.length === 0 ? (
          <div className="text-center p-8 text-muted-foreground">
            No faculty members found. Add your first faculty member to get started.
          </div>
        ) : filteredFaculty.length === 0 ? (
          <div className="text-center p-8 text-muted-foreground">
            No faculty members match the selected filters.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID Number</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Program</TableHead>
                <TableHead>Academic Rank | Position</TableHead>
                <TableHead>Max Units</TableHead>
                <TableHead>Specializations</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredFaculty.map((facultyMember) => (
                <TableRow key={facultyMember.id}>
                  <TableCell>
                    <Badge variant="outline">{facultyMember.idNumber}</Badge>
                  </TableCell>
                  <TableCell className="font-medium">{facultyMember.name}</TableCell>
                  <TableCell className="text-sm max-w-[200px] break-words whitespace-normal">{facultyMember.department}</TableCell>
                  <TableCell>
                    <Badge variant="secondary">{facultyMember.position}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="font-mono">
                      {facultyMember.maxUnits || 12} units
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">
                    {Array.isArray(facultyMember.specialization) ? (
                      <div className="flex flex-wrap gap-1">
                        {facultyMember.specialization.slice(0, 3).map((spec) => (
                          <Badge key={spec} variant="secondary" className="text-xs">
                            {getSpecializationLabel(spec)}
                          </Badge>
                        ))}
                        {facultyMember.specialization.length > 3 && (
                          <Badge variant="secondary" className="text-xs">
                            +{facultyMember.specialization.length - 3} more
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">{facultyMember.specialization}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(facultyMember)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(facultyMember.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}